import type { ProjectOverride } from "@/lib/board-projects";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowRightLeft, ChevronDown, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TaskCard } from "@/components/task-card";
import { cn } from "@/lib/utils";
import { sortTasks, type TaskSort } from "@/lib/task-sort";
import {
  kindColor,
  type BoardColumn,
  type BoardResponseCount,
  type ColumnKind,
  type Project,
  type Task,
  type User,
} from "@/lib/types";

/** `null` is every state at once; otherwise the id of the one state shown. */
type Filter = string | null;

export interface BoardListProps {
  columns: BoardColumn[];
  tasks: Task[];
  /** The board's sort, the same one its columns use. */
  sort: TaskSort;
  boardEndsAt: string | null;
  users: User[];
  now: number;
  mentionCounts: Map<string, number>;
  responseCounts: Map<string, BoardResponseCount>;
  projectOverrides: Map<string, ProjectOverride>;
  canAddTasks: boolean;
  onOpenTask: (taskId: string) => void;
  /** Appends the card to the end of the target state. */
  onMoveTask: (taskId: string, columnId: string, index: number) => void;
  onAddTask: (columnId: string) => void;
  onAddColumn: () => void;
  onRenameColumn: (column: BoardColumn) => void;
  onDeleteColumn: (column: BoardColumn) => void;
  /**
   * The cards are a filtered subset (only those waiting on the user), so a state
   * with none of them is left out rather than drawn as "Nothing here".
   */
  filtered?: boolean;
}

/**
 * The board for a screen too narrow for its columns — a phone, an iPad in
 * portrait. The same cards, stacked rather than side by side: a strip of states
 * across the top (every state's count visible at once, which is what a row of
 * 84vw columns could never show), and full-width cards beneath, grouped by state.
 *
 * A finger moves a card with the ⇄ button in its footer rather than by dragging: a
 * drag across a list that scrolls vertically has nowhere to be dropped that is
 * not also where the user is trying to scroll.
 */
export function BoardList({
  columns,
  tasks,
  sort,
  boardEndsAt,
  users,
  now,
  mentionCounts,
  responseCounts,
  projectOverrides,
  canAddTasks,
  onOpenTask,
  onMoveTask,
  onAddTask,
  onAddColumn,
  onRenameColumn,
  onDeleteColumn,
  filtered = false,
}: BoardListProps) {
  const [filter, setFilter] = useState<Filter>(null);
  // Finished work is the longest list and the least read, so it starts folded.
  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(columns.filter((column) => column.kind === "done").map((column) => column.id)),
  );

  const byColumn = useMemo(() => {
    const map = new Map<string, Task[]>(columns.map((column) => [column.id, []]));
    for (const task of tasks) map.get(task.columnId)?.push(task);
    for (const column of columns) {
      map.set(column.id, sortTasks(map.get(column.id)!, sort, { boardEndsAt, columnKind: column.kind }));
    }
    return map;
  }, [columns, tasks, sort, boardEndsAt]);

  // Each card's move menu, made once per change of cards or states rather than every
  // render: a new element each time defeated the card's memo, and a refetch anywhere
  // re-rendered every card of the list with its menu.
  const moveLatest = useRef({ onMoveTask, byColumn });
  moveLatest.current = { onMoveTask, byColumn };
  const moveMenus = useMemo(() => {
    const menus = new Map<string, ReactNode>();
    for (const task of tasks) {
      const onMove = (columnId: string) =>
        moveLatest.current.onMoveTask(task.id, columnId, moveLatest.current.byColumn.get(columnId)?.length ?? 0);
      menus.set(task.id, <MoveMenu task={task} columns={columns} onMove={onMove} />);
    }
    return menus;
  }, [tasks, columns]);

  // A state deleted while it was the filter falls back to everything.
  const active = filter !== null && columns.some((column) => column.id === filter) ? filter : null;
  const shown = (active === null ? columns : columns.filter((column) => column.id === active)).filter(
    (column) => !filtered || active !== null || (byColumn.get(column.id)?.length ?? 0) > 0,
  );

  const toggle = (columnId: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav
        aria-label="Filter by state"
        className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-border/70 bg-background/85 px-4 py-2 scrollbar-none win-sm:px-6"
      >
        <StateChip label="All" count={tasks.length} selected={active === null} onClick={() => setFilter(null)} />
        {columns.map((column) => (
          <StateChip
            key={column.id}
            label={column.name}
            count={byColumn.get(column.id)?.length ?? 0}
            tint={kindColor(column.kind)}
            selected={active === column.id}
            onClick={() => setFilter(column.id)}
          />
        ))}
      </nav>

      <div className="grain min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1rem,var(--safe-bottom,env(safe-area-inset-bottom)))] pt-3 scrollbar-slim win-sm:px-6">
        <div className="mx-auto max-w-5xl space-y-4">
          {shown.map((column) => {
            const list = byColumn.get(column.id) ?? [];
            // A single state picked from the strip is never folded: picking it was the
            // ask. Nor is anything under the waiting filter, which is an ask too.
            const folded = active === null && !filtered && collapsed.has(column.id);
            return (
              <section key={column.id} aria-label={`${column.name}, ${list.length} tasks`}>
                <StateHeader
                  column={column}
                  count={list.length}
                  folded={folded}
                  canFold={active === null}
                  canAddTasks={canAddTasks}
                  onToggle={() => toggle(column.id)}
                  onAddTask={() => onAddTask(column.id)}
                  onRename={() => onRenameColumn(column)}
                  onDelete={() => onDeleteColumn(column)}
                />
                {folded ? null : list.length === 0 ? (
                  // One line, not the column's tall box: in a stack, every empty state's
                  // height is a card pushed further down the screen.
                  <p className="rounded-md border border-dashed border-border/50 px-3 py-2 text-[12px] text-muted-foreground">
                    Nothing here
                  </p>
                ) : (
                  // grid-cols-1, not the implicit track: an `auto` track grows to the
                  // longest unbroken word (a pasted URL), pushing every card past the window.
                  <div className="grid grid-cols-1 gap-2 win-md:grid-cols-2">
                    {list.map((task) => (
                      <div key={task.id} className="animate-[enter_180ms_cubic-bezier(0.16,1,0.3,1)]">
                        <TaskCard
                          task={task}
                          columnKind={column.kind as ColumnKind}
                          users={users}
                          now={now}
                          openMentions={mentionCounts.get(task.id) ?? 0}
                          replies={responseCounts.get(task.id)}
                          projectOverride={projectOverrides.get(task.id)}
                          dragging={false}
                          onOpen={() => onOpenTask(task.id)}
                          action={moveMenus.get(task.id)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {active === null && !filtered ? (
            <button
              onClick={onAddColumn}
              className="w-full rounded-lg border border-dashed border-border/70 px-3 py-2.5 text-left text-[13px] text-muted-foreground transition-colors hover:border-ring/40 hover:bg-muted/40 hover:text-foreground"
            >
              + Add a state
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const StateChip = ({
  label,
  count,
  tint,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  tint?: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={cn(
      "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12.5px] font-medium transition-colors",
      selected
        ? "border-ring/60 bg-accent text-foreground"
        : "border-border/70 text-muted-foreground hover:bg-muted/50 hover:text-foreground",
    )}
  >
    {tint ? <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: tint }} /> : null}
    <span className="max-w-40 truncate">{label}</span>
    <span className="tabular-nums opacity-70">{count}</span>
  </button>
);

function StateHeader({
  column,
  count,
  folded,
  canFold,
  canAddTasks,
  onToggle,
  onAddTask,
  onRename,
  onDelete,
}: {
  column: BoardColumn;
  count: number;
  folded: boolean;
  canFold: boolean;
  canAddTasks: boolean;
  onToggle: () => void;
  onAddTask: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const atLimit = column.wipLimit !== null && count >= column.wipLimit;
  return (
    <header className="mb-1.5 flex items-center gap-1">
      <button
        type="button"
        onClick={canFold ? onToggle : undefined}
        disabled={!canFold}
        aria-expanded={canFold ? !folded : undefined}
        className="flex min-w-0 flex-1 items-center gap-2 rounded-md py-1.5 pr-2 text-left"
      >
        {canFold ? (
          <ChevronDown
            className={cn("size-4 shrink-0 text-muted-foreground transition-transform", folded && "-rotate-90")}
          />
        ) : null}
        <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ backgroundColor: kindColor(column.kind) }} />
        <h2 className="min-w-0 truncate text-[13.5px] font-semibold tracking-tight">{column.name}</h2>
        <span className={cn("text-[11.5px] font-semibold tabular-nums", atLimit ? "text-destructive" : "text-muted-foreground")}>
          {count}
          {column.wipLimit !== null ? `/${column.wipLimit}` : ""}
        </span>
      </button>
      {canAddTasks ? (
        <Button variant="ghost" size="icon-sm" onClick={onAddTask} aria-label={`Add task to ${column.name}`}>
          <Plus />
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`${column.name} options`}>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{column.key}</DropdownMenuLabel>
          <DropdownMenuItem onSelect={onRename}>
            <Pencil /> Edit state
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onSelect={onDelete}>
            <Trash2 /> Delete state
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

function MoveMenu({ task, columns, onMove }: { task: Task; columns: BoardColumn[]; onMove: (columnId: string) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Move "${task.title}" to another state`}
          className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        >
          <ArrowRightLeft className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Move to</DropdownMenuLabel>
        {columns.map((column) => (
          <DropdownMenuItem key={column.id} disabled={column.id === task.columnId} onSelect={() => onMove(column.id)}>
            <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ backgroundColor: kindColor(column.kind) }} />
            {column.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
